/**
 * Payment Slice — Types & Initial State
 *
 * Matches Smithy model: FintechLastMileBeaconSmithyModel/model/payment.smithy
 * API: GET /payment/{endToEndId}
 */

/** Payment metadata (top-level info). */
export interface PaymentMetaData {
  paymentId: string;
  dataSource: string;
  paymentType: string;
  bank: string;
  beaconPaymentStatus: string;
  beaconPaymentState: string;
  createdAt: number | null;
  updatedAt: number | null;
  stateUpdatedAt: number | null;
  statusRank: number | null;
  partnerEndToEndId: string | null;
}

/** Transformation details for the outbound file. */
export interface TransformationDetails {
  transformationTime: string | null;
  batchIndex: string | null;
  fileName: string | null;
  pluginName: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  pluginPrivilegedOperationsRequest: string | null;
}

/** A single event within a lifecycle stage. */
export interface StatusEvent {
  status: string;
  state: string;
  timestamp: number | null;
}

/** Detail for a lifecycle stage. */
export interface PaymentStatusDetail {
  enteredAt: number | null;
  exitedAt: number | null;
  latencyMs: number | null;
  state: string;
  paymentStatus: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  statuses: StatusEvent[];
}

/** A linked transmission (outbound file). */
export interface PaymentTransmission {
  executionArn: string | null;
  fileName: string | null;
  bank: string | null;
  beaconTransmissionState: string | null;
  messageType: string | null;
}

/** A bank response entry (PAIN_002, CAMT053, etc.). */
export interface BankResponseEntry {
  executionArn: string | null;
  fileName: string | null;
  bank: string | null;
  state: string | null;
  responseCode: string | null;
  responseDescription: string | null;
}

/** Full payment detail returned by GET /payment/{endToEndId}. */
export interface PaymentDetailData {
  paymentMetaData: PaymentMetaData;
  transformationDetails: TransformationDetails | null;
  statusMap: Record<string, PaymentStatusDetail>;
  transmissions: PaymentTransmission[];
  bankResponses: Record<string, BankResponseEntry[]>;
}

/** Redux slice state. */
export interface PaymentState {
  data: PaymentDetailData | null;
  loading: boolean;
  error: string | null;
  currentRequestId: string | null;
}

export const paymentInitialState: PaymentState = {
  data: null,
  loading: false,
  error: null,
  currentRequestId: null,
};
